import { ReactNode, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { AppointmentPerson } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import { AppointmentType, Provider } from '@weave/schema-gen-ts/dist/schemas/schedule/settings/v2/settings.pb';
import { EventStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { PersonTypes } from '@frontend/api-person';
import { ScheduleTypes } from '@frontend/api-schedule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { AppointmentStatusComponents } from '@frontend/appointment-status';
import { InfinitePaginatedList } from '@frontend/components';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { PanelContent } from '@frontend/panel-engine';
import {
  formatDateTime,
  useCalendarEventsConfigurationShallowStore,
  useCalendarEventsFormTypeShallowStore,
  useGetAppointmentColorsEnabled,
  useLocationTimezone,
} from '@frontend/schedule-helpers';
import { theme } from '@frontend/theme';
import {
  Chip,
  DatePickerField,
  DropdownField,
  FormFieldActionTypes,
  NakedButton,
  SpinningLoader,
  Text,
  TimeField,
  useAlert,
  useDebouncedValue,
  useForm,
  SearchField,
  useFormField,
} from '@frontend/design-system';
import { ColorDot } from '../../components/appointment-panel/color-dot';
import { ColorPickerModal } from '../../components/ColorPicker/ColorPickerModal';
import { ListBoxOption } from '../../components/list-box-popover/list-box-option.js';
import { MultiViewCombobox } from '../../components/multi-view-combobox';
import { ProviderAvatar } from '../../components/ServiceProviderAvatar';
import { AppointmentStatusV3Enum } from '../../components/v3/EventCardAppointmentStatusChip/types';
import {
  DATETIME_FORMATS,
  EVENT_BACKGROUND_COLOR,
  EVENT_BACKGROUND_COLOR_PALETTE,
  PRIMARY_EVENT_COLOR,
} from '../../constants';
import { usePastDateBanner, useSchedulingLocationInfo } from '../../hooks';
import { getLabelWithTimezoneAbbr } from '../../utils';
import { getDateString, getTimeDiff, getTodaysDate } from '../../utils/date-helpers';
import { CustomContactForm } from '../Calendar/EventsPanel/CalendarEvents/appointment/custom-contact-form';
import { usePatientSelector } from '../Calendar/EventsPanel/CalendarEvents/appointment/use-patient-selector';
import { calendarEventsTrackingIds } from '../Calendar/EventsPanel/CalendarEvents/calendar-events-tracking-ids';
import { RecommendedDurationBanner } from '../Calendar/EventsPanel/CalendarEvents/recommended-duration-banner';
import {
  AppointmentAvailabilityFormValues,
  OnCreateCalendarEvent,
  OnDeleteCalendarEvent,
  OnUpdateCalendarEvent,
} from '../Calendar/EventsPanel/CalendarEvents/types';
import { isUUID } from '../Calendar/utils';
import { PanelCloseButton, PanelFooter, PanelFormField, PanelHeader } from './panel-components';

dayjs.extend(utc);

export const CACHE_AND_STALE_TIME = 1000 * 60 * 5; // 5 minute

const MIN_APPT_DURATION = 30; // 30 minutes

export type AppointmentTypeExtended = AppointmentType & { active?: boolean };

export interface NewAppointmentFormProps {
  selectedLocationIds: string[];
  isScheduleV3: boolean;
  isStatusDropdownVisible?: boolean;
  appointmentTypes: AppointmentType[];
  providersList: Provider[];
  availableAppointmentTimes: ScheduleTypes.OpeningBlock[];
  isAvailableAppointmentTimesLoading: boolean;
  selectedLocationId: string;
  closeModal: () => void;
  refetchAppointmentTypes: () => void;
  setSelectedFormValuesForAvailabilityApi: (
    appointmentAvailabilityFormValues: AppointmentAvailabilityFormValues
  ) => void;
  onCreateAppointment: (payload: OnCreateCalendarEvent, apptStatus?: EventStatus) => void;
  // This onDeleteAppointment prop is used for V2 appointments only
  onDeleteAppointment?: ({ id, createdByLocationId }: OnDeleteCalendarEvent) => void; // for v3, we handle deletion from button component itself
  onUpdateAppointment: (payload: OnUpdateCalendarEvent, apptStatus?: EventStatus) => void;
  refetchAppointments?: (locationId?: string) => void | Promise<void>;
  setSelectedLocationId: (locationId: string) => void;
}

// NOTE: this is new AddEditAppointmentForm component which will replace NewAppointmentForm component
export const AddEditAppointmentForm = ({
  isScheduleV3,
  isStatusDropdownVisible,
  appointmentTypes,
  providersList,
  availableAppointmentTimes,
  isAvailableAppointmentTimesLoading,
  selectedLocationId,
  closeModal,
  refetchAppointmentTypes,
  onDeleteAppointment,
  onUpdateAppointment,
  onCreateAppointment,
  refetchAppointments,
  setSelectedFormValuesForAvailabilityApi,
  setSelectedLocationId,
}: NewAppointmentFormProps) => {
  const { t } = useTranslation('scheduleCalendarEvents');
  const alerts = useAlert();

  const { canUseAppointmentColors } = useGetAppointmentColorsEnabled();

  const [customDuration, setCustomDuration] = useState<number>(0);

  const { locationTimezoneAbbr } = useLocationTimezone(selectedLocationId);
  const { getScopeName, formatLocationTime, isMultiLocation, selectedLocationIds } = useSchedulingLocationInfo();

  const { hasWSM, wsmActiveLocationIds } = SchedulerV3.Helpers.useCheckHasWSM(selectedLocationId);
  const { invalidateListCalendarEvents } = SchedulerV3.Helpers.useSchedulerV3InvalidateQueries();

  const { configuredCalendarEvent, setConfiguredCalendarEvent } = useCalendarEventsConfigurationShallowStore(
    'configuredCalendarEvent',
    'setConfiguredCalendarEvent'
  );

  const isEditMode = !!configuredCalendarEvent?.eventId;
  const title = isEditMode ? t('Edit Appointment') : t('Add Appointment');
  const { selectedPerson, setSelectedPerson } = useCalendarEventsFormTypeShallowStore(
    'selectedPerson',
    'setSelectedPerson'
  );

  const [shouldShowFormErrors, setShouldShowFormErrors] = useState(false);
  const [isSavingAppointment, setIsSavingAppointment] = useState<boolean>(false);

  const [singleSourceId, setSingleSourceId] = useState<string>('');

  const useSelectedPerson = isScheduleV3 && selectedPerson !== null;

  const appointmentPerson = useMemo(() => {
    if (useSelectedPerson) {
      return {
        firstName: selectedPerson.FirstName,
        lastName: selectedPerson.LastName,
        personId: selectedPerson.PersonID,
        email: selectedPerson.Email,
        mobilePhone: selectedPerson.MobilePhone,
        homePhone: selectedPerson.HomePhone,
        sourceId: selectedPerson.SourceID,
        preferredName: selectedPerson.PreferredName ?? '',
        pmId: selectedPerson.PMID ?? '',
        pmDisplayId: selectedPerson.PMDisplayID ?? '',
      };
    }
    return {};
  }, [selectedPerson, useSelectedPerson]);

  const [personValue, setPersonValue] = useState<AppointmentPerson>(appointmentPerson ?? {});

  const [selectedProviderId, setSelectedProviderId] = useState<string>(configuredCalendarEvent?.providerId ?? '');
  const [providerSearchTerm, setProviderSearchTerm] = useState<string>('');

  const {
    isIntegratedOffice,
    data: integrations,
    isLoading: isLoadingIntegrationDetails,
  } = DataSourcesQueries.useGetAccessibleIntegrations();

  const shouldRenderSourceDropdown = isIntegratedOffice && (integrations ?? []).length > 1;

  const updateEndTime = (startTime: string, appointmentTypeValue = '') => {
    const appointmentTypeDuration =
      appointmentTypes?.find((type) => type.id === appointmentTypeValue)?.durationMinutes ?? 0;

    const duration = customDuration || appointmentTypeDuration || MIN_APPT_DURATION;

    return dayjs(startTime, 'HH:mm:ss').add(duration, 'minute');
  };

  const {
    getFieldProps,
    values,
    seedValues,
    reset: resetAppointmentForm,
    isComplete: isAppointmentFormComplete,
    validate: validateAppointmentForm,
  } = useForm({
    fields: {
      locationId: {
        type: 'dropdown',
        value: configuredCalendarEvent?.locationId || selectedLocationIds?.[0] || '',
        required: true,
        validator: ({ value }) =>
          !wsmActiveLocationIds.includes(value)
            ? t('Please select a location that has Appointment Creation access')
            : '',
      },
      source: {
        type: 'dropdown',
        value: configuredCalendarEvent?.createdBySourceId,
        required: shouldRenderSourceDropdown,
      },
      appointmentStatus: {
        type: 'dropdown',
        value: configuredCalendarEvent?.apptStatusV3 || AppointmentStatusV3Enum.UNCONFIRMED,
        required: true,
      },
      appointmentType: {
        type: 'dropdown',
        value: '',
        required: true,
      },
      backgroundColor: {
        type: 'text',
        value: configuredCalendarEvent?.backgroundColor || EVENT_BACKGROUND_COLOR,
        hidden: !canUseAppointmentColors,
      },
      appointmentDate: {
        type: 'datePicker',
        value: !!configuredCalendarEvent?.eventId
          ? configuredCalendarEvent.startDate
          : getDateString(configuredCalendarEvent?.calendarDateValue) || getTodaysDate(),
        required: true,
      },
      appointmentStartTime: {
        type: 'time',
        value:
          formatDateTime(configuredCalendarEvent?.startHour, {
            inputFormat: 'hh:mm A',
            outputFormat: 'h:mma',
          }) ?? '',
        required: true,
      },
      appointmentEndTime: {
        type: 'time',
        value:
          formatDateTime(configuredCalendarEvent?.endHour, {
            inputFormat: 'hh:mm A',
            outputFormat: 'h:mma',
          }) ?? '',
        required: true,
      },
    },
    fieldStateReducer: (state, action) => {
      if (action.type !== FormFieldActionTypes.Update) {
        return state;
      }

      if (action.payload.name === 'appointmentEndTime') {
        // Set the custom duration if the end time is changed
        if (state.appointmentStartTime && state.appointmentEndTime) {
          const duration = getTimeDiff(state.appointmentStartTime.value, state.appointmentEndTime.value, {
            crossDay: true,
          });

          setCustomDuration(duration);
        }
      } else if (action.payload.name === 'appointmentStartTime' || action.payload.name === 'appointmentType') {
        if (!state.appointmentStartTime.value) return state;

        const endTime = updateEndTime(state.appointmentStartTime.value, state.appointmentType.value);

        return {
          ...state,
          appointmentEndTime: {
            ...state.appointmentEndTime,
            error: '',
            value: endTime.format('HH:mm:ss'),
            displayValue: endTime.format('h:mma'),
          },
        };
      }
      return state;
    },
  });

  const searchFieldProps = useFormField({ type: 'text', value: '' });

  const debouncedSearchValue = useDebouncedValue(searchFieldProps.value, 300);

  const initialScrollTime = SchedulerV3.Helpers.useGetTimeFieldStartTime({
    date: values.appointmentDate,
    locationId: values.locationId ?? '',
  });

  const filteredAppointmentTypes = useMemo(() => {
    const sortedApptTypes = [...(appointmentTypes ?? [])].sort((a, b) => {
      const nameA = a.name?.toLowerCase() || '';
      const nameB = b.name?.toLowerCase() || '';
      return nameA.localeCompare(nameB);
    });
    if (!debouncedSearchValue) {
      return sortedApptTypes;
    }
    return sortedApptTypes.filter((appointmentType) =>
      appointmentType.name?.toLowerCase().includes(debouncedSearchValue.toLowerCase())
    );
  }, [appointmentTypes, debouncedSearchValue]);

  useEffect(() => {
    if (values.locationId) {
      setSelectedLocationId(values.locationId);
    }
  }, [values.locationId]);

  // Set the source if the location has a single source or is non integrated
  useEffect(() => {
    const isNonIntegratedOrSingleSource =
      integrations && integrations.length > 0 && !isScheduleV3 && (integrations.length === 1 || !isIntegratedOffice);

    if (isNonIntegratedOrSingleSource) {
      setSingleSourceId(integrations[0].SourceID);
      seedValues({
        ...values,
        source: integrations[0].SourceID,
      });
    }

    // TODO: Add logic to set the source tenant id for v3
  }, [integrations, isScheduleV3]);

  // Manage patient selection and creation
  const {
    infinitePersonQuery,
    isCreatingContact,
    isCreateContactDisabled,
    patientForm,
    selectedPatientOption,
    onSearchChange,
    onPrimaryAction,
    onPatientChange,
    onPatientRemove,
    setSelectedPatientOption,
  } = usePatientSelector({
    isIntegratedOffice,
    isScheduleV3,
    locationId: selectedLocationId,
    setPersonValue,
  });

  const primaryViewConfig = {
    title: t('Add New Contact'),
    primaryLabel: t('Save Contact'),
    primaryButtonTrackingId: calendarEventsTrackingIds.createNewContactButton,
    cancelButtonTrackingId: calendarEventsTrackingIds.cancelNewContactButton,
    content: <CustomContactForm form={patientForm} locationId={selectedLocationId} />,
    isLoading: isCreatingContact,
    isDisabled: isCreateContactDisabled,
    onAction: onPrimaryAction,
    onCancel: () => {},
  };

  const defaultViewConfig = {
    primaryButtonLabel: t('Add New Contact'),
    primaryButtonTrackingId: calendarEventsTrackingIds.addNewContactButton,
    cancelButtonTrackingId: calendarEventsTrackingIds.cancelContactButton,
    content: (
      <InfinitePaginatedList
        height={360}
        infiniteQueryProps={infinitePersonQuery}
        renderListItem={({ listItem }): ReactNode => {
          const { PersonID, Status } = listItem;

          // Parse birthdate in UTC and normalize to start of day to prevent timezone-related date shifts
          const birthdayObj = dayjs.utc(listItem.Birthdate).startOf('day');
          const birthdate = birthdayObj.isValid() ? birthdayObj.format('MM/DD/YYYY') : '-';

          return (
            <ListBoxOption key={PersonID} value={PersonID} onClick={() => selectPerson(listItem)}>
              <Text>{getFullName(listItem)}</Text>
              <Text size='small' color='light'>
                {Status !== 'Active' ? (
                  <Chip variant={Status === 'Prospective' ? 'rose' : 'neutral'}>{Status}</Chip>
                ) : (
                  birthdate
                )}
              </Text>
            </ListBoxOption>
          );
        }}
      />
    ),
    onAction: isIntegratedOffice || !isScheduleV3 ? undefined : () => {}, // show action button only for v3 core
  };

  useEffect(() => {
    const appointmentTypeId = appointmentTypes?.find(
      (type) => type?.name?.toLowerCase() === configuredCalendarEvent?.appointmentType?.toLowerCase()
    )?.id;

    if (!appointmentTypeId) {
      return;
    }

    seedValues({
      appointmentType: appointmentTypeId,
    });
  }, [appointmentTypes, configuredCalendarEvent?.appointmentType]);

  const clearAppointmentForm = ({ shouldClearSourceId = true }) => {
    resetAppointmentForm();
    refetchAppointmentTypes();
    setSelectedProviderId('');
    setSelectedPatientOption(undefined);
    setSelectedPerson(null);
    setConfiguredCalendarEvent(null);
    shouldClearSourceId && setSingleSourceId('');
  };

  const cancelButtonHandler = () => {
    clearAppointmentForm({ shouldClearSourceId: true });
    closeModal();
  };

  const selectPerson = (person: PersonTypes.Person) => {
    setPersonValue({
      firstName: person.FirstName,
      lastName: person.LastName,
      personId: person.PersonID,
      email: person.Email,
      mobilePhone: person.MobilePhone,
      homePhone: person.HomePhone,
      sourceId: person.SourceID,
      preferredName: person.PreferredName ?? '',
      pmId: person.PMID ?? '',
      pmDisplayId: person.PMDisplayID ?? '',
      gender: person.Gender,
      status: person.Status,
      birthDate: person.Birthdate,
    });
  };

  // Provider selection handlers
  const onProviderChange = (providerId: string | string[]) => {
    const id = Array.isArray(providerId) ? providerId[0] : providerId;
    setSelectedProviderId(id);
  };

  const onProviderRemove = () => {
    setSelectedProviderId('');
  };

  const onProviderSearch = (searchTerm: string) => {
    setProviderSearchTerm(searchTerm);
  };

  const submitAppointmentForm = async () => {
    if (!isFormValid) {
      validateAppointmentForm();
      setShouldShowFormErrors(true);
      return;
    }

    setIsSavingAppointment(true);
    const appointmentType = appointmentTypes?.find((type) => type.id === values.appointmentType);

    const appointmentDuration = getAppointmentDuration();

    const selectedProvider = providersList.find((provider) => provider.id === selectedProviderId);
    const practitionerName = getFullName(selectedProvider ?? {});

    try {
      const startDate = getAppointmentStartDate();
      const appointmentData: OnCreateCalendarEvent = {
        duration: appointmentDuration,
        locationId: selectedLocationId,
        personId: selectedPatientOption?.id || '',
        practitionerId: selectedProviderId,
        startDate,
        type: values.appointmentType || '',
        backgroundColor: values.backgroundColor || EVENT_BACKGROUND_COLOR,
      };

      if (configuredCalendarEvent?.eventId) {
        await onUpdateAppointment(
          {
            id: configuredCalendarEvent?.eventId,
            referenceId: configuredCalendarEvent?.referenceId,
            practitionerCalendarId: selectedProvider?.parentProviderId ?? '',
            person: personValue,
            appointmentTypeId: appointmentType?.id || '',
            appointmentTypeName: appointmentType?.name || '',
            practitionerName,
            ...appointmentData,
          },
          values.appointmentStatus as EventStatus
        );
        alerts.success(t('Successfully updated appointment'));
      } else {
        const appointmentProvider = providersList.find((provider) => provider.id === selectedProviderId);
        await onCreateAppointment(
          {
            ...appointmentData,
            practitionerName,
            person: personValue,
            practitionerCalendarId: appointmentProvider?.parentProviderId ?? '',
            sourceId: values?.source || singleSourceId,
            appointmentTypeId: values.appointmentType || '',
            appointmentTypeName: appointmentType?.name || '',
          },
          values.appointmentStatus as EventStatus
        );
        alerts.success(t('Successfully created appointment'));
      }
      setConfiguredCalendarEvent(null);
      // Use scheduler V3 invalidation to ensure fresh calendar event data
      invalidateListCalendarEvents({
        locationId: selectedLocationId,
        ignoreDate: true,
      });
      setSelectedPatientOption(undefined);
      setSelectedPerson(null);
      closeModal();
    } catch (error: any) {
      console.error(error);
      const errorMessage = configuredCalendarEvent?.eventId
        ? 'Failed to update appointment'
        : 'Failed to create appointment';
      alerts.error(errorMessage);
    } finally {
      setIsSavingAppointment(false);
    }
  };

  const filteredProvidersList = useMemo(() => {
    const providerListData = providersList.filter((provider) => isUUID(provider.id ?? ''));

    const filteredByLocation = providerListData.filter((provider) => {
      const locationIdsToFilterProviders = values.locationId ? [values.locationId] : selectedLocationIds;
      return locationIdsToFilterProviders.includes(provider.locationId ?? '');
    });

    if (!providerSearchTerm) {
      return filteredByLocation;
    }

    return filteredByLocation.filter((provider) => {
      const providerName = getFullName(provider).toLowerCase();
      return providerName.includes(providerSearchTerm.toLowerCase());
    });
  }, [providersList, selectedLocationIds, values.locationId, providerSearchTerm]);

  const selectedProviderOption = useMemo(() => {
    if (!selectedProviderId) return undefined;

    const provider = providersList.find((p) => p.id === selectedProviderId);
    if (!provider) return undefined;

    return {
      id: provider.id ?? '',
      label: getFullName(provider),
    };
  }, [selectedProviderId, providersList]);

  const customProviderList = useMemo(() => {
    if (values.appointmentType) {
      const availableProvidersByAppointmentTypes = appointmentTypes
        ?.find((appointmentType) => appointmentType.id === values.appointmentType)
        ?.providerAssets?.filter((providerAsset) => providerAsset.available);

      // Display providers who match the selected source id (if a provider has the source id property) and are available for the selected appointment type
      return providersList.filter(({ id, sourceId }) => {
        const isValidUUID = isUUID(id ?? '');
        if (!isValidUUID) return false;

        if (!!sourceId) {
          return availableProvidersByAppointmentTypes?.some(
            (providerAssetId) => providerAssetId.id === id && values.source === sourceId
          );
        } else {
          return availableProvidersByAppointmentTypes?.some((providerAssetId) => providerAssetId.id === id);
        }
      });
    }
    return [];
  }, [values.appointmentType, providersList, appointmentTypes, values.source]);

  const providerDefaultViewConfig = {
    primaryButtonLabel: undefined, // No primary action for providers for now
    primaryButtonTrackingId: '',
    cancelButtonTrackingId: '',
    content: (
      <div css={{ height: 360 }}>
        {customProviderList?.length > 0 && (
          <>
            <Text weight='bold' css={{ margin: theme.spacing(0, 1, 1, 2) }}>
              {t('Available Providers')}
            </Text>
            {customProviderList?.map((provider, index) => {
              const providerFullName = getFullName(provider);
              return (
                <ListBoxOption
                  key={(provider.id ?? '') + index}
                  value={provider.id ?? ''}
                  onClick={() => onProviderChange(provider.id ?? '')}
                  aria-checked={selectedProviderId === provider.id}
                  aria-disabled={!provider.isActive}
                >
                  <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                    {!provider.isActive ? (
                      <Icon size={24} name='archive' color='subdued' css={{ flexShrink: 0 }} />
                    ) : (
                      <ProviderAvatar provider={provider} size={3} key={provider.id} />
                    )}
                    <div css={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <Text as='span' size='medium' color={provider.isActive ? 'default' : 'subdued'}>
                        {providerFullName}
                      </Text>
                    </div>
                  </div>
                </ListBoxOption>
              );
            })}
          </>
        )}
        {!!customProviderList?.length && (
          <Text weight='bold' css={{ margin: theme.spacing(2, 1, 1, 2) }}>
            {t('Available Providers')}
          </Text>
        )}
        {filteredProvidersList.map((provider, index) => {
          const providerFullName = getFullName(provider);
          return (
            <ListBoxOption
              key={(provider.id ?? '') + index}
              value={provider.id ?? ''}
              onClick={() => onProviderChange(provider.id ?? '')}
              aria-checked={selectedProviderId === provider.id}
              aria-disabled={!provider.isActive}
            >
              <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                {!provider.isActive ? (
                  <Icon name='archive' color='subdued' css={{ flexShrink: 0 }} />
                ) : (
                  <ProviderAvatar provider={provider} size={3} key={provider.id} />
                )}
                <div css={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <Text as='span' size='medium' color={provider.isActive ? 'default' : 'subdued'}>
                    {providerFullName}
                  </Text>
                </div>
              </div>
            </ListBoxOption>
          );
        })}
      </div>
    ),
    onAction: undefined, // to hide action buttons
  };

  useEffect(() => {
    const hasFieldValues = !!(
      selectedProviderId &&
      values.appointmentType &&
      values.appointmentDate &&
      values.locationId
    );

    const duration = appointmentTypes?.find((type) => type.id === values.appointmentType)?.durationMinutes;

    if (hasFieldValues && duration) {
      const selectedProvider = providersList.find((provider) => provider.id === selectedProviderId);

      setSelectedFormValuesForAvailabilityApi({
        appointmentDate: values.appointmentDate ?? '',
        appointmentType: values.appointmentType ?? '',
        selectedLocationId: values.locationId ?? '',
        selectedPractitionerId: selectedProviderId,
        selectedPractitionerCalendarId: selectedProvider?.parentProviderId ?? '',
        duration,
      });
    }
  }, [
    selectedProviderId,
    values.appointmentType,
    values.appointmentDate,
    values.locationId,
    appointmentTypes,
    providersList,
    setSelectedFormValuesForAvailabilityApi,
  ]);

  const getAppointmentStartDate = () => {
    if (!values.appointmentStartTime || !values.appointmentDate || !values.locationId) {
      return '';
    }
    return isScheduleV3
      ? formatLocationTime(`${values.appointmentDate} ${values.appointmentStartTime}`, values.locationId, {
          toTimezone: 'UTC',
        })
      : dayjs(`${values.appointmentDate} ${values.appointmentStartTime}`)?.toISOString();
  };

  const getAppointmentDuration = () => {
    if (!values.appointmentStartTime || !values.appointmentEndTime || !values.appointmentDate) {
      return 0;
    }
    if (values.appointmentStartTime && !values.appointmentEndTime && values.appointmentType) {
      const appointmentType = appointmentTypes?.find((type) => type.id === values.appointmentType);
      return appointmentType?.durationMinutes ?? 0;
    }

    return getTimeDiff(values.appointmentStartTime, values.appointmentEndTime, { crossDay: true });
  };

  // for initial mount case, set customDuration and appointmentEndTime
  useEffect(() => {
    const startHour = configuredCalendarEvent?.startHour ?? '';
    const endHour = configuredCalendarEvent?.endHour ?? '';
    const isEndTimeSelected = !!endHour && startHour !== endHour;

    const initialCustomDuration = getTimeDiff(startHour, endHour, { format: 'hh:mm A', crossDay: true });
    setCustomDuration(isEndTimeSelected ? initialCustomDuration : 0);

    // For new event case, set appointmentEndTime based on customDuration
    if (!configuredCalendarEvent?.eventId && startHour) {
      const endTime = dayjs(startHour, 'hh:mm A')
        .add(isEndTimeSelected ? customDuration || MIN_APPT_DURATION : MIN_APPT_DURATION, 'minute') // keep min 30 mins when empty
        .format('h:mma');
      seedValues({ appointmentEndTime: endTime });
    }
  }, [configuredCalendarEvent, seedValues]);

  const appointmentTypeDuration = useMemo(() => {
    const appointmentType = appointmentTypes?.find((type) => type.id === values.appointmentType);
    return appointmentType?.durationMinutes ?? 0;
  }, [appointmentTypes, values.appointmentType]);

  const timeSelectionHelperText = () => {
    if (
      values.appointmentStartTime ||
      values.appointmentEndTime ||
      !values.locationId ||
      !values.appointmentType ||
      !values.appointmentDate
    ) {
      return null;
    }

    return (
      <Chip variant='disabled' css={{ maxWidth: 'max-content' }}>
        {t('No Available Times')}
      </Chip>
    );
  };

  const deleteCalendarsEventMutation = SchedulerV3.Mutations.useDeleteCalendarsEventMutation();

  const deleteAppointment = async () => {
    try {
      await deleteCalendarsEventMutation.mutateAsync({
        eventId: configuredCalendarEvent?.eventId || '',
        existingStartDate: formatDate(configuredCalendarEvent?.startDate, DATETIME_FORMATS.ISO_DATE),
        referenceId: configuredCalendarEvent?.referenceId,
      });
      alerts.success(t('Successfully deleted appointment'));
      closeModal();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      alerts.error(t('Failed to delete appointment. Please try again.'));
    }
  };

  const handleDeleteAppointmentEventTypeSelection = () => {
    if (isScheduleV3) {
      deleteAppointment();
    } else {
      closeModal();
      onDeleteAppointment?.({
        id: configuredCalendarEvent?.eventId || '',
        createdByLocationId: configuredCalendarEvent?.locationId || '',
      });
      refetchAppointments?.(configuredCalendarEvent?.locationId);
    }
  };

  const isEndTimeNextDay = useMemo(() => {
    const startTime = dayjs(`${values.appointmentDate} ${values.appointmentStartTime}`);
    const endTime = dayjs(`${values.appointmentDate} ${values.appointmentEndTime}`);

    if (!endTime.isAfter(startTime) && values.appointmentEndTime !== '00:00:00') {
      // If end time is not after start time, it is considered next day
      return true;
    }
    return false;
  }, [values.appointmentDate, values.appointmentStartTime, values.appointmentEndTime]);

  const nextDay = dayjs(values.appointmentDate).add(1, 'day').format('MMM Do');

  const hasFormTimeInputValues = !!values.appointmentStartTime && values.appointmentEndTime;

  const isFormValid =
    isAppointmentFormComplete && selectedProviderId && selectedPatientOption?.id && (isScheduleV3 || !isEndTimeNextDay);

  const { pastDateBannerComponent } = usePastDateBanner({
    initialDate: configuredCalendarEvent?.eventId ? configuredCalendarEvent?.calendarDateValue : '',
    currentDate: values.appointmentDate,
    bannerMessage: t(
      "Heads up! This appointment is scheduled in the past. If that's intentional, go ahead and click Save."
    ),
    locationId: values.locationId ?? '',
  });

  const appointmentStatusProps = getFieldProps('appointmentStatus');

  const handleStatusChange = (appointmentStatus: EventStatus) => {
    appointmentStatusProps.onChange({ name: 'appointmentStatus', value: appointmentStatus });
  };

  const shouldShowRecommendedDurationBanner =
    !configuredCalendarEvent?.eventId &&
    appointmentTypeDuration !== customDuration &&
    !!values.appointmentStartTime &&
    !!values.appointmentEndTime;

  return (
    <>
      <PanelCloseButton onClick={closeModal} />
      <PanelHeader title={title} />
      <PanelContent padding={false} css={panelContentStyle}>
        {isMultiLocation && (
          <PanelFormField label={t('Location')}>
            <DropdownField
              disabled={isEditMode}
              label=''
              css={{ input: { cursor: isEditMode ? 'not-allowed' : 'default' } }}
              {...getFieldProps('locationId')}
            >
              {selectedLocationIds.map((locationId) => {
                const locationName = getScopeName(locationId);
                return (
                  <DropdownField.Option key={locationId} value={locationId} searchValue={locationName ?? ''}>
                    <div
                      css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}
                      onClick={() => clearAppointmentForm({ shouldClearSourceId: false })}
                    >
                      <Icon name='location' size={16} color='light' />
                      <Text color={isEditMode ? 'disabled' : 'default'}>{locationName}</Text>
                    </div>
                  </DropdownField.Option>
                );
              })}
            </DropdownField>
          </PanelFormField>
        )}
        {/* // TODO: label should be token base */}
        <PanelFormField label={t('Patient')}>
          <MultiViewCombobox
            showAvatar
            disabled={isEditMode}
            error={shouldShowFormErrors && !selectedPatientOption ? t('Patient is required') : ''}
            searchPlaceholder={t('Search Contacts')}
            value={selectedPatientOption ? [selectedPatientOption] : []}
            defaultViewConfig={defaultViewConfig}
            primaryViewConfig={primaryViewConfig}
            onChange={onPatientChange}
            onRemove={onPatientRemove}
            onSearch={onSearchChange}
          />
        </PanelFormField>
        {/* // TODO: confirm whether to display it or not */}
        {isStatusDropdownVisible && (
          <PanelFormField label={t('Status')}>
            <AppointmentStatusComponents.EditableStatusChip
              status={appointmentStatusProps.value}
              onStatusChange={handleStatusChange}
            />
          </PanelFormField>
        )}
        <PanelFormField label={t('Appointment Details')}>
          <div css={{ display: 'flex', gap: theme.spacing(2), '> *:first-child': { flex: 1 } }}>
            <DropdownField label='' placeholder={t('Select Appointment Type')} {...getFieldProps('appointmentType')}>
              <SearchField
                {...searchFieldProps}
                css={{ margin: theme.spacing(0.5) }}
                name='dropdown-search-appointment-type'
                autoFocus
                clearable
              />
              {filteredAppointmentTypes?.map((type) => {
                return (
                  <DropdownField.Option key={type.id} value={type.id || ''}>
                    <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                      <ColorDot color={type.appointmentTypeLabelColor || PRIMARY_EVENT_COLOR} />
                      {type.name}
                    </div>
                  </DropdownField.Option>
                );
              })}
            </DropdownField>
            {canUseAppointmentColors && (
              <ColorPickerModal
                selectedColor={values.backgroundColor ?? EVENT_BACKGROUND_COLOR}
                palette={EVENT_BACKGROUND_COLOR_PALETTE}
                closeOnSelect={true}
                onChange={(color) =>
                  getFieldProps('backgroundColor').onChange({ value: color, name: 'backgroundColor' })
                }
              />
            )}
          </div>
          <div css={{ marginTop: theme.spacing(2) }}>
            <MultiViewCombobox
              error={shouldShowFormErrors && !selectedProviderId ? t('Provider is required') : ''}
              searchPlaceholder={t('Search Providers')}
              value={selectedProviderOption ? [selectedProviderOption] : []}
              defaultViewConfig={providerDefaultViewConfig}
              leftIconName='user-small'
              onChange={onProviderChange}
              onRemove={onProviderRemove}
              onSearch={onProviderSearch}
            />
          </div>
          {/* // TODO: add operatory */}
        </PanelFormField>
        {shouldRenderSourceDropdown && (
          <PanelFormField label={t('Source')}>
            <DropdownField label={t('Select Source')} disabled={isEditMode} {...getFieldProps('source')}>
              {integrations?.map((source) => {
                return (
                  <DropdownField.Option key={source.SourceID} value={source.SourceID}>
                    {source.SourceName}
                  </DropdownField.Option>
                );
              })}
            </DropdownField>
          </PanelFormField>
        )}
        <PanelFormField label={getLabelWithTimezoneAbbr(t('Date & Time'), locationTimezoneAbbr)}>
          <DatePickerField
            label=''
            placeholder={t('Select Date')}
            {...getFieldProps('appointmentDate')}
            css={{ maxWidth: '100%' }}
          />
          <div css={{ display: 'flex', gap: theme.spacing(2), marginTop: theme.spacing(1) }}>
            <TimeField
              {...getFieldProps('appointmentStartTime')}
              label=''
              placeholder={t('Start Time')}
              initialScrollTime={initialScrollTime}
              showAdornment
              interval={5}
              disabled={!values.appointmentDate}
            />
            <div>
              <TimeField
                {...getFieldProps('appointmentEndTime')}
                label=''
                placeholder={t('End Time')}
                initialScrollTime={initialScrollTime}
                showAdornment
                interval={5}
                hasError={isEndTimeNextDay && !isScheduleV3}
                disabled={!values.appointmentDate}
              />
              {!!values.appointmentDate && hasFormTimeInputValues && isEndTimeNextDay && (
                <Text
                  size='small'
                  css={{
                    color: isScheduleV3 ? theme.colors.indigo50 : theme.colors.critical50,
                    marginTop: theme.spacing(0.5),
                    gridArea: '2/2',
                  }}
                >
                  {isScheduleV3
                    ? t('Ends {{nextDay}} (next day)', { nextDay })
                    : t('End time must be after start time')}
                </Text>
              )}
            </div>
          </div>
        </PanelFormField>
        <RecommendedDurationBanner
          hideBanner={!shouldShowRecommendedDurationBanner}
          appointmentTypeDuration={appointmentTypeDuration}
          selectedDuration={customDuration}
          onUpdate={(duration: number) => {
            setCustomDuration(0);
            seedValues({
              appointmentEndTime: dayjs(values.appointmentStartTime, 'HH:mm:ss')
                .add(duration, 'minute')
                .format('HH:mm:ss'),
            });
          }}
        />
        {!values.appointmentStartTime && !!selectedProviderId && !!values.appointmentType && (
          <div css={[fieldContainerStyles, timeFieldContainerStyles]}>
            <Text>{t('Available Times')}</Text>
            <div css={[availableTimeSlotContainer(!!values.appointmentStartTime)]}>
              {isAvailableAppointmentTimesLoading ? (
                <SpinningLoader size='small' css={{ maxWidth: 100 }} />
              ) : !availableAppointmentTimes?.length ? (
                timeSelectionHelperText()
              ) : (
                availableAppointmentTimes?.map((time) => (
                  <NakedButton
                    key={time.startTime}
                    onClick={() => {
                      const appointmentStartTime = formatDate(time.startTime, 'HH:mm:ss');
                      const endTime = updateEndTime(appointmentStartTime, values.appointmentType);
                      const appointmentEndTime = formatDate(endTime, 'HH:mm:ss');
                      seedValues({ appointmentStartTime, appointmentEndTime });
                    }}
                    css={[
                      timeButtonStyles,
                      values.appointmentStartTime === formatDate(time.startTime, 'HH:mm:ss') &&
                        selectedTimeButtonStyles,
                    ]}
                  >
                    {formatDate(time.startTime, 'h:mma')}
                  </NakedButton>
                ))
              )}
            </div>
          </div>
        )}
      </PanelContent>
      {pastDateBannerComponent}
      <PanelFooter
        hideDelete={!isEditMode || !hasWSM}
        onDelete={handleDeleteAppointmentEventTypeSelection}
        onCancel={cancelButtonHandler}
        saveLabel={isSavingAppointment ? t('Saving...') : t('Save')}
        onSave={submitAppointmentForm}
        saveDisabled={isSavingAppointment || isLoadingIntegrationDetails}
      />
    </>
  );
};

const availableTimeSlotContainer = (isTimeSelected: boolean) =>
  css({
    display: 'grid',
    gridTemplateColumns: 'auto auto',
    maxHeight: '190px',
    overflowY: 'auto',
    ...(isTimeSelected && {
      filter: 'opacity(0.5)',
      transition: 'filter 0.2s ease-in-out',
      ':hover': { filter: 'opacity(1)' },
    }),
  });
const fieldContainerStyles = css({
  display: 'grid',
  gridTemplateColumns: 'minmax(auto, 35%) minmax(auto, 280px)',
  alignItems: 'center',
  margin: theme.spacing(3, 0),
});

const timeFieldContainerStyles = css({
  alignItems: 'flex-start',
});

const timeButtonStyles = css({
  marginRight: theme.spacing(1),
  marginBottom: theme.spacing(1),
  padding: theme.spacing(0.5),
  border: `1px solid ${theme.colors.neutral50}`,
  borderRadius: theme.borderRadius.small,
});

const selectedTimeButtonStyles = css({
  border: `1px solid ${theme.colors.primary50}`,
  backgroundColor: theme.colors.primary10,
  color: theme.colors.primary60,
});

const panelContentStyle = css({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'auto',
  padding: theme.spacing(3),
  gap: theme.spacing(3),
});
